/**
 * LikeButton component with optimistic updates
 * Displays heart icon that can be clicked to like/unlike a photo
 */

import { Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { useLikePhoto, useUnlikePhoto } from '../../services/social';
import { cn } from '../../lib/utils';

interface LikeButtonProps {
  photoId: string;
  isLiked: boolean;
  likeCount: number;
  showCount?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LikeButton({
  photoId,
  isLiked,
  likeCount,
  showCount = true,
  size = 'md',
  className,
}: LikeButtonProps) {
  const likeMutation = useLikePhoto();
  const unlikeMutation = useUnlikePhoto();

  const handleToggleLike = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers

    if (isLiked) {
      unlikeMutation.mutate(photoId);
    } else {
      likeMutation.mutate(photoId);
    }
  };

  const isLoading = likeMutation.isPending || unlikeMutation.isPending;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          sizeClasses[size],
          'transition-all',
          isLiked && 'text-red-500 hover:text-red-600',
          !isLiked && 'text-gray-600 hover:text-red-500'
        )}
        onClick={handleToggleLike}
        disabled={isLoading}
        aria-label={isLiked ? 'Unlike photo' : 'Like photo'}
      >
        <Heart
          size={iconSizes[size]}
          className={cn('transition-all', isLiked && 'fill-current', isLoading && 'animate-pulse')}
        />
      </Button>

      {showCount && (
        <span
          className={cn(
            'font-medium',
            isLiked ? 'text-red-500' : 'text-gray-700',
            size === 'sm' && 'text-sm',
            size === 'md' && 'text-base',
            size === 'lg' && 'text-lg'
          )}
        >
          {likeCount} {likeCount === 1 ? 'like' : 'likes'}
        </span>
      )}
    </div>
  );
}
